import type { AxiosInstance } from 'axios';
import { environment } from '@/config/environment';
import { jsonHeaders } from '@/api/axiosInstance';
import type { Order, PagedResponse, Address } from '@/types';
import { buildRequestContext, withRecordId, withSource } from '@/services/requestContext';

type ApiOrderPayload = Record<string, unknown>;

type ApiOrderProductPayload = Record<string, unknown>;

export type OrdersHandoffAction = 'open-details' | 'create-new';

export interface OrdersListHandoff {
  source: 'orders-list';
  action: OrdersHandoffAction;
  orderId?: string;
  businessContext?: Record<string, unknown>;
  previousUrl?: string;
}

const normalizeString = (value: unknown) => String(value ?? '').trim();

const buildNonApiControllerUrl = (path: string) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  try {
    const apiUrl = new URL(environment.apiEndpoint, window.location.origin);
    apiUrl.pathname = apiUrl.pathname.replace(/\/api\/?$/i, '') || '/';
    return new URL(normalizedPath, apiUrl.toString()).toString();
  } catch {
    return normalizedPath;
  }
};

const INCOTERM_DESCRIPTION_BY_CODE: Record<string, string> = {
  DDP: 'Delivered Duty Paid',
};

const ORDER_TYPE_DESCRIPTION_BY_CODE: Record<string, string> = {
  ZOR: 'Sales Order',
  OR: 'Sales Order',
  UB: 'Stock Order',
  NB: 'Stock Order',
};

export const getOrderTypeDescription = (code: string | undefined | null): string | undefined => {
  const normalizedCode = normalizeString(code).toUpperCase();
  return ORDER_TYPE_DESCRIPTION_BY_CODE[normalizedCode];
};

const extractRequestedTimeFromDateTime = (value: unknown): string | undefined => {
  const normalizedValue = normalizeString(value);
  if (!normalizedValue) {
    return undefined;
  }

  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return undefined;
  }

  const hours24 = parsedDate.getUTCHours();
  const minutes = parsedDate.getUTCMinutes();
  if (hours24 === 0 && minutes === 0) {
    return undefined;
  }

  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const parseRequestedTime = (value: unknown): { hours: number; minutes: number } | null => {
  const normalizedValue = normalizeString(value).toUpperCase();
  if (!normalizedValue) {
    return null;
  }

  const amPmMatch = normalizedValue.match(/^(\d{1,2}):(\d{2})(?:\s)?([AP]M)$/);
  if (amPmMatch) {
    const rawHours = Number(amPmMatch[1]);
    const minutes = Number(amPmMatch[2]);
    const period = amPmMatch[3];
    if (rawHours < 1 || rawHours > 12 || minutes < 0 || minutes > 59) {
      return null;
    }

    const hours = period === 'PM'
      ? (rawHours === 12 ? 12 : rawHours + 12)
      : (rawHours === 12 ? 0 : rawHours);

    return { hours, minutes };
  }

  const twentyFourHourMatch = normalizedValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (twentyFourHourMatch) {
    const hours = Number(twentyFourHourMatch[1]);
    const minutes = Number(twentyFourHourMatch[2]);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }

    return { hours, minutes };
  }

  return null;
};

const buildRequestedDeliveryDateTime = (dateValue: string | undefined | null, timeValue: string | undefined | null) => {
  const normalizedDateValue = normalizeString(dateValue);
  if (!normalizedDateValue) {
    return null;
  }

  const requestedDate = new Date(`${normalizedDateValue}T00:00:00.000Z`);
  if (Number.isNaN(requestedDate.getTime())) {
    return normalizedDateValue;
  }

  const parsedTime = parseRequestedTime(timeValue);
  if (parsedTime) {
    requestedDate.setUTCHours(parsedTime.hours, parsedTime.minutes, 0, 0);
  }

  return requestedDate.toISOString();
};

export const createDetailsHandoff = (
  orderId: string,
  businessContext?: Record<string, unknown>,
  previousUrl?: string,
): OrdersListHandoff => ({
  source: 'orders-list',
  action: 'open-details',
  orderId: normalizeString(orderId),
  businessContext,
  previousUrl: normalizeString(previousUrl) || undefined,
});

export const createNewOrderHandoff = (
  businessContext?: Record<string, unknown>,
  previousUrl?: string,
): OrdersListHandoff => ({
  source: 'orders-list',
  action: 'create-new',
  businessContext,
  previousUrl: normalizeString(previousUrl) || undefined,
});

export const adaptOrdersHandoff = (value: unknown): OrdersListHandoff | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const raw = value as Record<string, unknown>;
  const source = normalizeString(raw.source);
  const action = normalizeString(raw.action) as OrdersHandoffAction;

  if (source !== 'orders-list' || (action !== 'open-details' && action !== 'create-new')) {
    return null;
  }

  const orderId = normalizeString(raw.orderId);

  if (action === 'open-details' && !orderId) {
    return null;
  }

  if (action === 'create-new' && orderId) {
    return null;
  }

  return {
    source: 'orders-list',
    action,
    orderId: action === 'open-details' ? orderId : undefined,
    businessContext: raw.businessContext && typeof raw.businessContext === 'object'
      ? (raw.businessContext as Record<string, unknown>)
      : undefined,
    previousUrl: normalizeString(raw.previousUrl) || undefined,
  };
};

export const createNewOrderDraft = (creatorId?: string): Order => ({
  orderId: '',
  orderNumber: undefined,
  creatorId: normalizeString(creatorId) || undefined,
  orderType: '',
  orderTypeDescription: 'Sales Order',
  status: 'OC-Draft',
  preCyberStatus: '',
  orderPlacedDate: new Date().toISOString(),
  requestedDeliveryDate: '',
  customerId: '',
  customerName: '',
  products: [],
  shipToAddress: {
    name: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  },
  customerConfirmation: false,
  criticalCustomer: false,
  isPickup: false,
});

const adaptOrderProduct = (item: ApiOrderProductPayload, index: number): Order['products'][number] => {
  const rawLineNumber = item.lineNumber ?? item.itemNumber ?? item.itemNo;
  const parsedLineNumber = Number(rawLineNumber);

  const rawQuantity = item.quantity ?? item.qty ?? 0;
  const parsedQuantity = Number(rawQuantity);

  return {
    lineNumber: Number.isFinite(parsedLineNumber) && parsedLineNumber > 0 ? parsedLineNumber : index + 1,
    itemNumber: String(rawLineNumber ?? '').trim() || String(index + 1),
    materialCode: String(item.materialCode ?? item.material ?? '').trim(),
    description: String(item.materialDescription ?? item.description ?? '').trim(),
    materialDescription: String(item.materialDescription ?? item.description ?? '').trim(),
    quantity: Number.isFinite(parsedQuantity) ? parsedQuantity : 0,
    salesUOM: String(item.salesUOM ?? item.uoM ?? item.overallUoM ?? item.unitOfMeasure ?? 'MT').trim(),
    unitOfMeasure: String(item.uoM ?? item.salesUOM ?? item.overallUoM ?? item.unitOfMeasure ?? 'MT').trim(),
    customerMaterialNumber: String(item.customerMaterialNumber ?? '').trim() || undefined,
    gomMaterialCode: String(item.gomMaterialCode ?? '').trim() || undefined,
    gomMaterialDescription: String(item.gomMaterialDescription ?? '').trim() || undefined,
    batchNumber: String(item.batchNumber ?? item.batch ?? '').trim() || undefined,
    plantCode: String(item.plantCode ?? item.shipToPlantCode ?? item.supplyPointId ?? '').trim() || undefined,
    storageLocation: String(item.storageLocation ?? item.titleLocationId ?? '').trim() || undefined,
    price: item.materialPrice != null ? Number(item.materialPrice) : undefined,
    currency: String(item.currency ?? '').trim() || undefined,
    perAmount: item.perAmount != null ? Number(item.perAmount) : undefined,
    perUoM: String(item.perUoM ?? item.per ?? '').trim() || undefined,
  };
};


export const adaptOrder = (item: ApiOrderPayload): Order => {
  const isPickupBool = item.isPickup === true || item.isPickup === 'true';
  const requestedDeliveryDateTime = (item.requestedDeliveryDate as string) ?? '';
  const shipToAddressLine1 = String(item.shipToPlantAddress1 ?? '').trim();
  const shipToAddressLine2 = String(item.shipToPlantAddress2 ?? '').trim();
  const notifyPartnerAddressLine1 = String(item.notifyPartnerAddress1 ?? '').trim();
  const notifyPartnerAddressLine2 = String(
    item.notifyPartnerAddress2
    ?? item.notifyPartnerAddressLine2
    ?? item.notifyPartnerAddr2
    ?? '',
  ).trim();
  const notifyPartnerCity = String(item.notifyPartnerCity ?? '').trim();
  const notifyPartnerState = String(item.notifyPartnerState ?? '').trim();
  const notifyPartnerCountry = String(item.notifyPartnerCountry ?? '').trim();
  const notifyPartnerPostalCode = String(item.notifyPartnerZipCode ?? '').trim();
  const notifyPartnerEmail = String(item.notifyPartnerEMail ?? '').trim();
  const notifyPartnerCode = String(item.notifyPartnerId ?? '').trim();
  const notifyPartnerName = String(item.notifyPartnerName ?? '').trim();
  const incotermCode = String(item.incoterms1 ?? item.inco1 ?? item.incoterm ?? '').trim() || undefined;
  const mappedIncotermDescription = String(
    item.incoterms1Description
      ?? item.incotermsDescription
      ?? item.inco1Description
      ?? item.incoDescription
      ?? item.incotermDescription
      ?? item.inco1Desc
      ?? item.incoterms1Desc
      ?? '',
  ).trim() || undefined;
  const incotermDescription = mappedIncotermDescription
    ?? (incotermCode ? INCOTERM_DESCRIPTION_BY_CODE[incotermCode] : undefined);

  const shipToAddress: Address = {
    line1: shipToAddressLine1,
    line2: shipToAddressLine2,
    name: (item.shipToPlantName as string) ?? '',
    street: [(item.shipToPlantAddress1 as string), (item.shipToPlantAddress2 as string)]
      .filter(Boolean)
      .join(', '),
    city: (item.shipToPlantCity as string) ?? '',
    state: (item.shipToPlantState as string) ?? '',
    postalCode: (item.shipToPlantZipCode as string) ?? '',
    country: (item.shipToPlantCountry as string) ?? '',
  };

  const rawOrderTypeCode = String(
    item.orderTypeCode
      ?? item.orderTypeCodeValue
      ?? item.salesOrderType
      ?? '',
  ).trim();
  const rawOrderType = String(item.orderType ?? '').trim();
  const orderTypeDescription = String(
    getOrderTypeDescription(rawOrderTypeCode)
      ?? item.orderTypeDescription
      ?? item.orderTypeName
      ?? (rawOrderTypeCode && !getOrderTypeDescription(rawOrderTypeCode) ? rawOrderType : ''),
  ).trim();
  const normalizedOrderTypeCode = rawOrderTypeCode
    || (getOrderTypeDescription(rawOrderType) ? rawOrderType : '')
    || (rawOrderType.toLowerCase() === 'sales order' ? 'ZOR' : '');

  return {
    orderId: (item.orderNumber as string) ?? '',
    orderNumber: (item.orderNumber as string) ?? undefined,
    creatorId: (item.orderCreatorName as string) ?? undefined,
    orderType: normalizedOrderTypeCode,
    orderTypeCode: normalizedOrderTypeCode || undefined,
    orderTypeDescription: orderTypeDescription || (normalizedOrderTypeCode ? 'Sales Order' : undefined),
    status: ((item.cyberOrderStatus ?? item.orderStatus) as Order['status']) ?? 'Draft',
    preCyberStatus: (item.preCyberOrderStatus as string) ?? undefined,
    orderPlacedDate: (item.orderPlacedDate as string) ?? '',
    requestedDeliveryDate: isPickupBool ? '' : requestedDeliveryDateTime,
    requestedPickupDate: isPickupBool ? requestedDeliveryDateTime || undefined : undefined,
    requestedDeliveryTime: (item.requestedDeliveryTime as string) ?? extractRequestedTimeFromDateTime(requestedDeliveryDateTime),
    deliveryDate: (item.orderChangedDate as string) ?? undefined,
    customerId: (item.customerSoldToCustomerNo as string) ?? (item.soldToNumber as string) ?? '',
    soldToNumber: (item.customerSoldToCustomerNo as string) ?? (item.soldToNumber as string) ?? undefined,
    customerName: (item.customerSoldToName as string) ?? (item.customerName as string) ?? '',
    customerConfirmation:
      item.customerConfirmation == null ? undefined : item.customerConfirmation === 'Y',
    salesOrg: (item.sellingSalesOrg as string) ?? undefined,
    sellingSalesOrg: (item.sellingSalesOrg as string) ?? undefined,
    shippingCondition: (item.shippingCondition as string) ?? (item.customerShippingCondition as string) ?? undefined,
    shippingConditionDescription: (item.shippingConditionDescription as string) ?? undefined,
    modeOfTransportation: (item.modeOfTransportation as string) ?? undefined,
    products: [],
    shipToAddress,
    incoterm: incotermCode,
    incotermDescription,
    incotermLocation: (item.incoterms2 as string) ?? (item.incotermLocation as string) ?? undefined,
    freightForwarder: (item.custFreightForwarderId as string) ?? undefined,
    freightForwarderId: (item.custFreightForwarderId as string) ?? undefined,
    drcCode: (item.custDrcCodeId as string) ?? undefined,
    drcCodeId: (item.custDrcCodeId as string) ?? undefined,
    drcCodeDesc: (item.custDrcCodeDesc as string) ?? undefined,
    drcCodeCountry: (item.custDrcCodeCountry as string) ?? undefined,
    drcCodeRegion: (item.custDrcCodeRegion as string) ?? undefined,
    purchaseOrderNumber: (item.purchaseOrder as string) ?? (item.purchaseOrderNumber as string) ?? undefined,
    criticalCustomer:
      item.criticalCustomer == null ? undefined : item.criticalCustomer === 'Y',
    notifyPartnerCode: notifyPartnerCode || undefined,
    notifyPartnerName: notifyPartnerName || undefined,
    notifyPartner: {
      partnerId: notifyPartnerCode,
      partnerName: notifyPartnerName,
      notifyPartnerNumber: notifyPartnerCode || undefined,
      name: notifyPartnerName || undefined,
      address1: notifyPartnerAddressLine1 || undefined,
      address2: notifyPartnerAddressLine2 || undefined,
      city: notifyPartnerCity || undefined,
      state: notifyPartnerState || undefined,
      country: notifyPartnerCountry || undefined,
      zipCode: notifyPartnerPostalCode || undefined,
      eMail: notifyPartnerEmail || undefined,
      address: {
        name: notifyPartnerName,
        line1: notifyPartnerAddressLine1 || undefined,
        line2: notifyPartnerAddressLine2 || undefined,
        street: notifyPartnerAddressLine1,
        city: notifyPartnerCity,
        state: notifyPartnerState,
        postalCode: notifyPartnerPostalCode,
        country: notifyPartnerCountry,
      },
    },
    specialInstructions: (item.internalText as string) ?? undefined,
    haulierText: (item.shippingHaulierText as string) ?? undefined,
    manualInvoiceNumber: (item.manualInvoiceNumber as string) ?? undefined,
    createdAt: (item.orderPlacedDate as string) ?? undefined,
    updatedAt: (item.orderChangedDate as string) ?? undefined,
    isPickup: isPickupBool,
    preCyberOrderNumber: (item.preCyberOrderNumber as string) ?? undefined,
    preCyberOrderStatus: (item.preCyberOrderStatus as string) ?? undefined,
    responsibleEmployeeName: (item.responsibleEmployeeName as string) ?? undefined,
    supplyPointCode: (item.supplyPointId as string) ?? undefined,
    supplyPointName: (item.supplyPointName1 as string) ?? undefined,
    supplyPointAddress1: (item.supplyPointAddress1 as string) ?? undefined,
    supplyPointAddress2: (item.supplyPointAddress2 as string) ?? undefined,
    supplyPointCity: (item.supplyPointCity as string) ?? undefined,
    supplyPointState: (item.supplyPointState as string) ?? undefined,
    supplyPointZipCode: (item.supplyPointZipCode as string) ?? undefined,
    supplyPointCountry: (item.supplyPointCountry as string) ?? undefined,
    supplyPointTimeZone: (item.supplyPointTimeZone as string) ?? undefined,
    routeCode: (item.routeCode as string) ?? undefined,
    routeDescription: (item.routeDescription as string) ?? undefined,
    transitDays: item.transitDays != null ? Number(item.transitDays) : undefined,
    leadTime: item.leadTime != null ? Number(item.leadTime) : undefined,
    plannedLoadDate: (item.plannedLoadingDate as string) ?? undefined,
    transportationPlannedDate: (item.plannedTransportationDate as string) ?? undefined,
    carrierNumber: (item.carrierNumber as string) ?? undefined,
    responsibleEmployee: (item.responsibleEmployee as string) ?? undefined,
    contactName: (item.contactName as string) ?? (item.customerContactName as string) ?? undefined,
    contactEmail: (item.contactEmail as string) ?? (item.email as string) ?? undefined,
    contactPhoneNumber: (item.contactPhoneNumber as string) ?? (item.telephone as string) ?? undefined,
    distributionChannel: (item.distributionChannel as string) ?? undefined,
    division: (item.division as string) ?? undefined,
    shipToNumber: (item.shipToNumber as string) ?? (item.shipToPlantCode as string) ?? undefined,
    shipToName: (item.shipToPlantName as string) ?? undefined,
    shipToAddressLine1: shipToAddressLine1 || undefined,
    shipToAddressLine2: shipToAddressLine2 || undefined,
    shipToCity: (item.shipToPlantCity as string) ?? undefined,
    shipToState: (item.shipToPlantState as string) ?? undefined,
    shipToPostalCode: (item.shipToPlantZipCode as string) ?? undefined,
    shipToCountry: (item.shipToPlantCountry as string) ?? undefined,
    shipToPlantCode: (item.shipToPlantCode as string) ?? undefined,
    shipToPlantName: (item.shipToPlantName as string) ?? undefined,
    numberOfTrucks: item.numberOfTrucks != null ? Number(item.numberOfTrucks) : undefined,
    containerType: (item.containerType as string) ?? undefined,
    numberOfContainers: item.numberOfContainers != null ? Number(item.numberOfContainers) : undefined,
    uniqueRCIDNumber: (item.uniqueRCIDNumber as string) ?? undefined,
    shipmentId: (item.shipmentId as string) ?? undefined,
    freightPayerNumber: (item.freightPayerNumber as string) ?? undefined,
    vesselName: (item.vesselName as string) ?? undefined,
    expectedDischargeDate: (item.expectedDischargeDate as string) ?? undefined,
    loadLocationId: (item.loadLocationId as string) ?? undefined,
    overrideLoadDate: (item.overrideLoadDateMBL as string) ?? undefined,
    surveyInspector: (item.surveyInspector as string) ?? undefined,
    vesselScreeningNumber: (item.vesselScreeningNumber as string) ?? undefined,
    dischargeLocationId: (item.dischargeLocationId as string) ?? undefined,
    consigneePartner: (item.consigneePartner as string) ?? (item.consigneeCode as string) ?? (item.stoConsignee as string) ?? undefined,
    consigneeCode: (item.consigneeCode as string) ?? (item.consigneePartner as string) ?? (item.stoConsignee as string) ?? undefined,
    consigneeName: (item.consigneeName as string) ?? (item.stoConsigneeName as string) ?? undefined,
    consigneeAddressLine1: (item.consigneeAddressLine1 as string) ?? undefined,
    consigneeAddressLine2: (item.consigneeAddressLine2 as string) ?? undefined,
    consigneeCity: (item.consigneeCity as string) ?? undefined,
    consigneeState: (item.consigneeState as string) ?? undefined,
    consigneeCountry: (item.consigneeCountry as string) ?? undefined,
    consigneePostalCode: (item.consigneePostalCode as string) ?? undefined,
    consigneeEmail: (item.consigneeEmail as string) ?? undefined,
  };
};


export const deadaptOrder = (order: Order, source: string): ApiOrderPayload => {
  const isPickupBool = order.isPickup === true;

  const requestedDeliveryDate = isPickupBool
    ? buildRequestedDeliveryDateTime(order.requestedPickupDate, order.requestedDeliveryTime)
    : buildRequestedDeliveryDateTime(order.requestedDeliveryDate, order.requestedDeliveryTime);

  return {
    orderNumber: order.orderNumber ?? null,
    orderTypeCode: order.orderTypeCode ?? order.orderType ?? null,
    orderType: order.orderTypeDescription ?? null,
    cyberOrderStatus: order.status ?? null,
    orderStatus: order.status ?? null,
    orderCreatorName: order.creatorId ?? null,
    orderPlacedDate: order.orderPlacedDate ?? new Date().toISOString(),
    orderChangedDate: new Date().toISOString(),
    modeOfTransportation: order.modeOfTransportation ?? null,
    shippingCondition: order.shippingCondition
      ? String(order.shippingCondition).split(' - ')[0]
      : null,
    incoterms1: order.incoterm ? String(order.incoterm).split(' - ')[0] : null,
    incoterms2: order.incotermLocation ?? null,
    internalText: order.specialInstructions ?? null,
    isPickup: isPickupBool,
    requestedDeliveryDate,
    preCyberOrderStatus: order.preCyberStatus ?? order.preCyberOrderStatus ?? null,
    customerConfirmation:
      order.customerConfirmation == null ? null : order.customerConfirmation ? 'Y' : 'N',
    // Customer
    customerSoldToCustomerNo: order.customerId ?? order.soldToNumber ?? null,
    customerSoldToName: order.customerName ?? null,
    purchaseOrder: order.purchaseOrderNumber ?? null,
    criticalCustomer:
      order.criticalCustomer == null ? null : order.criticalCustomer ? 'Y' : 'N',
    notifyPartnerId: order.notifyPartnerCode
      ?? order.notifyPartner?.notifyPartnerNumber
      ?? order.notifyPartner?.partnerId
      ?? null,
    notifyPartnerName: order.notifyPartnerName
      ?? order.notifyPartner?.name
      ?? order.notifyPartner?.partnerName
      ?? null,
    notifyPartnerAddress1: order.notifyPartner?.address1
      ?? order.notifyPartner?.address?.line1
      ?? order.notifyPartner?.address?.street
      ?? null,
    notifyPartnerAddress2: order.notifyPartner?.address2
      ?? order.notifyPartner?.address?.line2
      ?? null,
    notifyPartnerCity: order.notifyPartner?.city
      ?? order.notifyPartner?.address?.city
      ?? null,
    notifyPartnerState: order.notifyPartner?.state
      ?? order.notifyPartner?.address?.state
      ?? null,
    notifyPartnerZipCode: order.notifyPartner?.zipCode
      ?? order.notifyPartner?.address?.postalCode
      ?? null,
    notifyPartnerCountry: order.notifyPartner?.country
      ?? order.notifyPartner?.address?.country
      ?? null,
    notifyPartnerEMail: order.notifyPartner?.eMail ?? null,
    sellingSalesOrg: order.salesOrg ?? order.sellingSalesOrg ?? null,
    // Supply Point
    supplyPointId: order.supplyPointCode ?? null,
    supplyPointName1: order.supplyPointName ?? null,
    supplyPointAddress1: order.supplyPointAddress1 ?? null,
    supplyPointAddress2: order.supplyPointAddress2 ?? null,
    supplyPointCity: order.supplyPointCity ?? null,
    supplyPointState: order.supplyPointState ?? null,
    supplyPointZipCode: order.supplyPointZipCode ?? null,
    supplyPointCountry: order.supplyPointCountry ?? null,
    supplyPointTimeZone: order.supplyPointTimeZone ?? null,
    // Route
    routeCode: order.routeCode ?? null,
    routeDescription: order.routeDescription ?? null,
    transitDays: order.transitDays ?? null,
    leadTime: order.leadTime ?? null,
    // Planned dates
    plannedLoadingDate: order.plannedLoadDate ?? null,
    plannedTransportationDate: order.transportationPlannedDate ?? null,
    // ShipTo
    shipToNumber: order.shipToNumber ?? null,
    shipToName: order.shipToName ?? null,
    shipToPlantCode: order.shipToPlantCode ?? order.shipToNumber ?? null,
    shipToPlantName: order.shipToPlantName ?? order.shipToName ?? order.shipToAddress?.name ?? null,
    shipToPlantAddress1: order.shipToAddressLine1 ?? order.shipToAddress?.line1 ?? order.shipToAddress?.street ?? null,
    shipToPlantAddress2: order.shipToAddressLine2 ?? order.shipToAddress?.line2 ?? null,
    shipToPlantCity: order.shipToCity ?? order.shipToAddress?.city ?? null,
    shipToPlantState: order.shipToState ?? order.shipToAddress?.state ?? null,
    shipToPlantZipCode: order.shipToPostalCode ?? order.shipToAddress?.postalCode ?? null,
    shipToPlantCountry: order.shipToCountry ?? order.shipToAddress?.country ?? null,
    // Freight / DRC
    custFreightForwarderId: order.freightForwarderId ?? order.freightForwarder ?? null,
    custDrcCodeId: order.drcCodeId ?? order.drcCode ?? null,
    custDrcCodeDesc: order.drcCodeDesc ?? null,
    custDrcCodeCountry: order.drcCodeCountry ?? null,
    custDrcCodeRegion: order.drcCodeRegion ?? null,
    // Contact / Responsible
    responsibleEmployee: order.responsibleEmployee ?? null,
    responsibleEmployeeName: order.responsibleEmployeeName ?? null,
    contactName: order.contactName ?? null,
    contactEmail: order.contactEmail ?? null,
    contactPhoneNumber: order.contactPhoneNumber ?? null,
    shippingHaulierText: order.haulierText ?? null,
    manualInvoiceNumber: order.manualInvoiceNumber ?? null,
    carrierNumber: order.carrierNumber ?? null,
    distributionChannel: order.distributionChannel ?? null,
    division: order.division ?? null,
    preCyberOrderNumber: order.preCyberOrderNumber ?? null,
    numberOfTrucks: order.numberOfTrucks ?? null,
    containerType: order.containerType ?? null,
    numberOfContainers: order.numberOfContainers ?? null,
    uniqueRCIDNumber: order.uniqueRCIDNumber ?? null,
    shipmentId: order.shipmentId ?? null,
    freightPayerNumber: order.freightPayerNumber ?? '',
    vesselName: order.vesselName ?? null,
    expectedDischargeDate: order.expectedDischargeDate ?? null,
    loadLocationId: order.loadLocationId ?? null,
    overrideLoadDateMBL: order.overrideLoadDate ?? null,
    surveyInspector: order.surveyInspector ?? null,
    vesselScreeningNumber: order.vesselScreeningNumber ?? null,
    dischargeLocationId: order.dischargeLocationId ?? null,
    consigneePartner: order.consigneePartner ?? order.consigneeCode ?? '',
    consigneeCode: order.consigneeCode ?? order.consigneePartner ?? '',
    consigneeName: order.consigneeName ?? null,
    consigneeAddressLine1: order.consigneeAddressLine1 ?? null,
    consigneeAddressLine2: order.consigneeAddressLine2 ?? null,
    consigneeCity: order.consigneeCity ?? null,
    consigneeState: order.consigneeState ?? null,
    consigneeCountry: order.consigneeCountry ?? null,
    consigneePostalCode: order.consigneePostalCode ?? null,
    consigneeEmail: order.consigneeEmail ?? null,
    source,
  };
};

export interface ListOrdersParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  sort?: string;
}

export type ListOrdersResult = PagedResponse<Order>;

export interface GetOrderParams {
  orderNumber: string;
}

export interface GetOrderResult {
  order: Order | null;
}

export interface ListOrderProductsResult {
  products: Order['products'];
}

export interface SaveOrderParams {
  order: Order;
}

export interface SaveOrderResult {
  order: Order | null;
}

export interface SaveOrderProductsParams {
  orderNumber: string;
  order: Order;
  products: Order['products'];
}

export interface DeleteOrderProductParams {
  orderNumber: string;
  itemNumber: string;
}

export interface CalculatePlanningDatesParams {
  routeCode: string;
  requestedDeliveryDate: Date;
}

export interface CalculateDeliveryDateParams {
  routeCode: string;
  plannedTransportationDate: Date;
}

export interface PlanningDatesResult {
  plannedLoadDate: string | null;
  plannedTransportationDate: string | null;
}

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORTS = [
  { columnName: 'OrderPlacedDate', value: 'DESC' },
  { columnName: 'RequestedDeliveryDate', value: 'ASC' },
];

type ColumnNameValue = {
  columnName: string;
  value: string;
};

const normalizeOrderSearch = (search?: string) => search ?? '';

const parseSortString = (sort?: string): ColumnNameValue[] => {
  if (!sort?.trim()) {
    return DEFAULT_SORTS;
  }

  const [columnName, direction] = sort.trim().split(/\s+/);
  if (!columnName) {
    return DEFAULT_SORTS;
  }

  return [{
    columnName,
    value: direction?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
  }];
};

const buildListParams = (
  source: string,
  pageNumber: number,
  linesPerPage: number | null,
  filter = '',
  filters: ColumnNameValue[] = [],
  sorts: ColumnNameValue[] = [],
  orderStatus: string[] = [],
) => ({
  UserId: 'UNIT_TEST',
  ProductType: null,
  Source: source,
  RecordId: null,
  SearchFilters: filter,
  OrderStatus: orderStatus,
  LinesPerPage: linesPerPage,
  PageNumber: pageNumber,
  Filters: filters,
  Sorting: sorts.length > 0 ? sorts : DEFAULT_SORTS,
});

const deadaptOrderProduct = (
  product: Order['products'][number],
  order: Order,
  orderNumber: string,
  source: string,
): ApiOrderProductPayload => ({
  containerType: order.containerType ?? null,
  currency: product.currency ?? null,
  customerMaterialNumber: product.customerMaterialNumber ?? null,
  dischargeLocationId: order.dischargeLocationId ?? null,
  dischargePort: null,
  expectedDischargeDate: order.expectedDischargeDate ?? null,
  gomMaterialCode: product.gomMaterialCode ?? null,
  gomMaterialDescription: product.gomMaterialDescription ?? null,
  inspectionCompany: null,
  itemGrossWeight: -99.99,
  itemNumber: product.itemNumber === '-' ? null : (product.itemNumber ?? null),
  loadLocationId: order.loadLocationId ?? null,
  materialCode: product.materialCode ?? null,
  materialDescription: product.description ?? product.materialDescription ?? null,
  materialPrice: product.price ?? null,
  numberOfContainers: order.numberOfContainers ?? 0,
  numberOfTrucks: order.numberOfTrucks != null ? Number(order.numberOfTrucks).toFixed(1) : 0,
  orderItemDateChanged: null,
  orderNumber,
  originPort: null,
  overallUoM: product.unitOfMeasure ?? null,
  plannedLoadingDate: order.requestedPickupDate ?? null,
  preCyberNominationKey: null,
  purchasingGroup: null,
  purchasingOrg: null,
  quantity: Number.isFinite(Number(product.quantity)) ? Number(product.quantity).toFixed(3) : 0,
  requestedDeliveryDate: order.requestedDeliveryDate || null,
  routeCode: order.routeCode ?? null,
  routeDescription: order.routeDescription ?? null,
  salesUOM: product.salesUOM ?? null,
  scheduler: null,
  shippingCondition: order.shippingCondition ? String(order.shippingCondition).split(' - ')[0] : null,
  shipToPlantCode: order.shipToNumber ?? null,
  supplyPointId: order.supplyPointCode ?? null,
  surveyInspector: null,
  titleLocationId: product.storageLocation ?? null,
  totalGrossWeight: -99.99,
  transitDays: order.transitDays ?? 0,
  vehicleNumber: null,
  vehicleText: null,
  vesselNameVoyageNumber: order.vesselName ?? null,
  vesselScreeningNumber: order.vesselScreeningNumber ?? null,
  uoM: product.unitOfMeasure ?? null,
  source,
  perAmount: product.perAmount ?? null,
  perUoM: product.perUoM ?? null,
});

export const listOrders = async (
  client: AxiosInstance,
  params: ListOrdersParams,
  source: string,
): Promise<ListOrdersResult> => {
  const response = await client.get('/simpleorder', {
    params: {
      SearchFilters: normalizeOrderSearch(params.search),
      PageNumber: params.pageNumber ?? 1,
      LinesPerPage: params.pageSize ?? DEFAULT_PAGE_SIZE,
      Sorting: parseSortString(params.sort),
    },
  });
  const body = response.data as Partial<ListOrdersResult> | undefined;

  return {
    data: Array.isArray(body?.data) ? body.data.map((item) => adaptOrder(item as unknown as ApiOrderPayload)) : [],
    count: typeof body?.count === 'number' ? body.count : 0,
  };
};

export const getOrder = async (
  client: AxiosInstance,
  params: GetOrderParams,
  source: string,
  userId?: string,
): Promise<GetOrderResult> => {
  const response = await client.get(`/Order/${encodeURIComponent(params.orderNumber)}`);
  const body = response.data as unknown;

  if (!Array.isArray(body) || body.length === 0) {
    return { order: null };
  }

  return {
    order: adaptOrder(body[0] as ApiOrderPayload),
  };
};

export const listOrderProducts = async (
  client: AxiosInstance,
  params: GetOrderParams,
  source: string,
  userId?: string,
): Promise<ListOrderProductsResult> => {
  const requestContext = withRecordId(
    buildRequestContext({ source, userId }),
    params.orderNumber,
  );

  const response = await client.get('/orderproduct', {
    params: {
      OrderNumber: params.orderNumber,
      RecordId: requestContext.RecordId,
    },
  });
  const body = response.data as unknown;

  return {
    products: Array.isArray(body)
      ? body.map((item, index) => adaptOrderProduct(item as ApiOrderProductPayload, index))
      : [],
  };
};

export const saveOrder = async (
  client: AxiosInstance,
  params: SaveOrderParams,
  source: string,
): Promise<SaveOrderResult> => {
  const apiPayload = deadaptOrder(params.order, source);
  const response = await client.post('/Order/update', [apiPayload], {
    headers: jsonHeaders(),
  });
  const body = response.data as unknown;

  if (!Array.isArray(body) || body.length === 0) {
    return { order: null };
  }

  return {
    order: adaptOrder(body[0] as ApiOrderPayload),
  };
};

export const saveOrderProducts = async (
  client: AxiosInstance,
  params: SaveOrderProductsParams,
  source: string,
): Promise<ListOrderProductsResult> => {
  const payload = params.products.map((product) => deadaptOrderProduct(
    product,
    params.order,
    params.orderNumber,
    source,
  ));

  const response = await client.patch('/orderproduct', payload);
  const body = response.data as unknown;

  return {
    products: Array.isArray(body)
      ? body.map((item, index) => adaptOrderProduct(item as ApiOrderProductPayload, index))
      : [],
  };
};

export const deleteOrderProduct = async (
  client: AxiosInstance,
  params: DeleteOrderProductParams,
  source: string,
): Promise<void> => {
  await client.delete('/orderproduct', {
    params: {
      OrderNumber: params.orderNumber,
      ItemNumber: params.itemNumber,
    },
  });
};

export const calculatePlanningDates = async (
  client: AxiosInstance,
  params: CalculatePlanningDatesParams,
  source: string,
): Promise<PlanningDatesResult> => {
  const response = await client.get<Partial<PlanningDatesResult>>('/plannedtransportationdates', {
    params: {
      RouteCode: params.routeCode,
      RequestedDeliveryDate: params.requestedDeliveryDate,
    },
  });

  return {
    plannedLoadDate: typeof response.data?.plannedLoadDate === 'string' ? response.data.plannedLoadDate : null,
    plannedTransportationDate: typeof response.data?.plannedTransportationDate === 'string'
      ? response.data.plannedTransportationDate
      : null,
  };
};

export const calculateDeliveryDate = async (
  client: AxiosInstance,
  params: CalculateDeliveryDateParams,
  source: string,
): Promise<string | null> => {
  const response = await client.get<unknown>(buildNonApiControllerUrl('/planningdates/requesteddate'), {
    params: {
      RouteCode: params.routeCode,
      PlannedTransportationDate: params.plannedTransportationDate.toISOString(),
    },
  });

  if (typeof response.data === 'string') {
    return response.data;
  }

  if (response.data instanceof Date) {
    return response.data.toISOString();
  }

  return null;
};
